import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientChartAPI } from '../../lib/api';
import { 
  Users,
  MagnifyingGlass,
  User,
  Calendar,
  VideoCamera,
  Stethoscope,
  CaretRight,
  FolderOpen,
  Table,
  ChartBar
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PatientListPage() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      const response = await patientChartAPI.getPatientsList();
      setPatients(response.data.patients);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredPatients = patients.filter(patient => {
    const search = searchTerm.toLowerCase();
    return (
      patient.first_name?.toLowerCase().includes(search) ||
      patient.last_name?.toLowerCase().includes(search) ||
      patient.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="patient-list-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
            <FolderOpen size={24} className="text-teal-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Patient Records
          </h1>
        </div>
        <p className="text-slate-500">Access and manage patient charts and medical records</p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search patients by name or email..."
            className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            data-testid="patient-search"
          />
        </div>
      </div>

      {/* Patient List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={20} className="text-slate-500" />
              <span className="font-medium text-slate-700">
                {filteredPatients.length} Patient{filteredPatients.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        <div className="divide-y divide-slate-100">
          {filteredPatients.map(patient => (
            <div
              key={patient.id}
              className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
              data-testid={`patient-row-${patient.id}`}
            >
              <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate(`/staff/patients/${patient.id}/dashboard`)}>
                <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-bold">
                  {patient.first_name?.[0]}{patient.last_name?.[0]}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">
                    {patient.first_name} {patient.last_name}
                  </p>
                  <p className="text-sm text-slate-500">{patient.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                {/* Visit Count */}
                <div className="text-center">
                  <p className="text-lg font-bold text-slate-700">{patient.total_visits || 0}</p>
                  <p className="text-xs text-slate-500">Visits</p>
                </div>

                {/* Last Visit */}
                <div className="text-right min-w-[120px]">
                  <div className="flex items-center gap-1 justify-end mb-1">
                    {patient.last_visit_type === 'telehealth' ? (
                      <VideoCamera size={14} className="text-purple-500" />
                    ) : patient.last_visit ? (
                      <Stethoscope size={14} className="text-blue-500" />
                    ) : (
                      <Calendar size={14} className="text-slate-400" />
                    )}
                    <span className="text-sm text-slate-600">
                      {formatDate(patient.last_visit)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">Last visit</p>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/staff/patients/${patient.id}/dashboard`);
                    }}
                    className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 flex items-center gap-1"
                    title="Patient Dashboard"
                  >
                    <Table size={14} />
                    Dashboard
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/staff/patients/${patient.id}/chart`);
                    }}
                    className="px-3 py-1.5 bg-slate-200 text-slate-700 text-xs rounded-lg hover:bg-slate-300 flex items-center gap-1"
                    title="Patient Chart"
                  >
                    <ChartBar size={14} />
                    Chart
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredPatients.length === 0 && (
            <div className="text-center py-12">
              <Users size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {searchTerm ? 'No patients match your search' : 'No patients found'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
